import React from "react"
import api from "config/axiosConfig"
import { apiRoutes } from "util/apiRoutes";

type SupportedEntityType = "issue" | "impact" | "solution" | "action" | "feedback" | "user" | "team"

interface EntitySentimentBarProps {
  entityType: SupportedEntityType
  entityId: number | null | undefined
  className?: string
  /**
   * Rounding precision in percent points for rendering widths and label.
   * For example, 1 => nearest 1%%. Values <= 0 will default to 1.
   */
  precisionPct?: number
  /**
   * Optional z/alpha/beta forwarded to the API; primarily for ranking views.
   */
  z?: number
  alpha?: number | null
  beta?: number | null
  /**
   * Whether to show counts next to the percentage label.
   */
  showCounts?: boolean
  /**
   * When this value changes, the component will re-fetch summary data.
   * Pass the current user sentiment or a monotonically increasing token from parent.
   */
  refreshKey?: number | string
}

interface SentimentSummary {
  n: number
  pos: number
  neg: number
  neu: number
  p_positive: number | null
  net_index: number | null
  wilson_lb: number | null
  p_smooth: number | null
}

const clampPercent = (value: number): number => {
  if (value < 0) return 0
  if (value > 100) return 100
  return value
}

const normalizePrecision = (value: number): number => {
  return value > 0 ? value : 1
}

/**
 * Compact two-color sentiment bar showing the negative share on the left (red)
 * and positive share on the right (green). Neutrals are excluded from the
 * denominator and reported separately via counts when requested.
 */
const EntitySentimentBar: React.FC<EntitySentimentBarProps> = ({
  entityType,
  entityId,
  className,
  precisionPct = 1,
  z,
  alpha,
  beta,
  showCounts = true,
  refreshKey,
}) => {
  const [summary, setSummary] = React.useState<SentimentSummary | null>(null)
  const [loading, setLoading] = React.useState<boolean>(false)

  React.useEffect(() => {
    let mounted = true
    const fetchSummary = async () => {
      if (!entityId) return
      try {
        setLoading(true)
        const { data } = await api.get(apiRoutes.entities.sentimentAverage, {
          params: {
            entityType,
            entityId,
            ...(typeof z === "number" ? { z } : {}),
            ...(typeof alpha === "number" ? { alpha } : {}),
            ...(typeof beta === "number" ? { beta } : {}),
          },
        })
        if (!mounted) return
        setSummary(data as SentimentSummary)
      } catch {
        if (mounted) setSummary(null)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    void fetchSummary()
    return () => {
      mounted = false
    }
  }, [entityType, entityId, z, alpha, beta, refreshKey])

  // Compute negative share with rounding to requested precision.
  const precision = normalizePrecision(precisionPct)
  const pos = summary?.pos ?? 0
  const neg = summary?.neg ?? 0
  const denom = pos + neg
  const rawNegPct = denom > 0 ? (neg / denom) * 100 : 0
  const roundedNegPct = clampPercent(Math.round(rawNegPct / precision) * precision)
  const roundedPosPct = clampPercent(100 - roundedNegPct)

  // Label strings (rounded to requested precision; default 1%)
  const labelPct = roundedNegPct
  const posLabelPct = roundedPosPct
  const n = denom
  const showPlaceholder = n === 0
  const useCounts = n > 10
  const centerLabel = showPlaceholder
    ? 'Sentiment'
    : (useCounts ? `${neg} | ${pos}` : `${labelPct}% | ${posLabelPct}%`)
  const ariaText = showPlaceholder
    ? 'Sentiment'
    : (useCounts
        ? `${neg} negative | ${pos} positive (n=${n})`
        : `${labelPct}% negative | ${posLabelPct}% positive (n=${n})`)

  return (
    <div className={`entity-sentiment-bar-container flex items-center ${className || ""}`} aria-live="polite">
      {/* Track */}
      <div
        className={`entity-sentiment-bar-track relative h-5 w-16 sm:w-24 rounded-full overflow-hidden flex ${showPlaceholder ? 'bg-eoe-icon dark:bg-eoe-dark-icon' : 'bg-eoe-icon dark:bg-eoe-dark-icon'}`}
        role="img"
        aria-label={ariaText}
        title={ariaText}
      >
        {!showPlaceholder && (
          <>
            {/* Negative segment on the left */}
            <div
              className="entity-sentiment-bar-negative-segment h-full bg-red-600"
              style={{ width: `${roundedNegPct}%` }}
            />
            {/* Positive segment fills the rest on the right */}
            <div
              className="entity-sentiment-bar-positive-segment h-full bg-green-600 flex-1"
              style={{ width: `${roundedPosPct}%` }}
            />
          </>
        )}
        {/* Centered label */}
        <div className="entity-sentiment-bar-label absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className={`text-xxs font-medium leading-none ${showPlaceholder ? 'text-eoe-text dark:text-eoe-dark-text' : 'text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]'}`}>
            {centerLabel}
          </span>
        </div>
      </div>

      {/* Optional counts */}
      <div className="entity-sentiment-bar-counts text-xxs text-eoe-text-sub dark:text-eoe-dark-text-sub tabular-nums">
        {showCounts && !loading && n > 0 ? <span className="ml-0">(n={n})</span> : null}
        {showCounts && loading ? "—" : null}
      </div>
    </div>
  )
}

export default EntitySentimentBar


